import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black_iconOnly } from '../../../components/Logo/Logo';
import { getTutorialIconLabel } from '../utils/appDisplayUtils';
import './AppsStorePage.css';
import SkeletonLoader from './SkeletonLoader';

//rebuild frontend and backedn
const AppsStorePage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingApp, setEditingApp] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  const userName = user?.name || user?.first_name || (user?.email ? user.email.split('@')[0] : 'Guest');
  const userEmail = user?.email || '';

  // Fetch apps from API
  useEffect(() => {
    const fetchApps = async () => {
      try {
        setLoading(true);
        const data = await appsAPI.getApps();
        setApps(data);
      } catch (error) {
        console.error('Error fetching apps:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApps();
  }, []);

  // Productivity apps (CV Builder, Cover Letter Builder, My Path, etc.)
  const allProductivityApps = apps.filter(
    (app) => app.app_type === 'tutorial' || app.app_type === 'productivity' || app.app_type === 'utility'
  );
  const topProductivityApps = allProductivityApps;
  
  // Group experience apps by subject (exclude productivity and tool)
  const experienceApps = apps.filter(app => app.app_type === 'case');
  const appsBySubject = experienceApps.reduce((acc, app) => {
    const subject = app.subject || 'Other';
    if (!acc[subject]) {
      acc[subject] = [];
    }
    acc[subject].push(app);
    return acc;
  }, {});

  // Create app bundles (3 cases + 1 test per subject)
  const createAppBundles = () => {
    const bundles = [];
    Object.entries(appsBySubject).forEach(([subject, subjectApps]) => {
      // Get 3 experience apps for this subject
      const cases = subjectApps.slice(0, 3);
      // Get productivity app for this subject
      const test = allProductivityApps.find(p => p.subject === subject);
      
      if (cases.length > 0) {
        bundles.push({
          subject,
          cases,
          test
        });
      }
    });
    return bundles;
  };

  const appBundles = createAppBundles();

  // Handle edit
  const handleEdit = (app) => {
    setEditingApp(app.id);
    setEditForm({
      description: app.description || '',
      rating: app.rating !== undefined ? app.rating : 4.5,
      rating_count: app.rating_count !== undefined ? app.rating_count : 0,
      has_video_interview: app.has_video_interview,
      has_logical_reasoning: app.has_logical_reasoning,
      has_pattern_recognition: app.has_pattern_recognition,
      has_numerical_reasoning: app.has_numerical_reasoning,
      has_verbal_reasoning: app.has_verbal_reasoning,
    });
  };

  // Handle save
  const handleSave = async (appId) => {
    try {
      setSaving(true);
      await appsAPI.updateApp(appId, editForm);
      
      // Refresh apps
      const updatedApps = await appsAPI.getApps();
      setApps(updatedApps);
      
      setEditingApp(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating app:', error);
      alert('Failed to update app. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditingApp(null);
    setEditForm({});
  };

  // Handle app click
  const handleAppClick = (appId) => {
    navigate(`/apps-store/${appId}`);
  };

  // Render productivity app icon (tool apps show image; others show text label)
  const renderProductivityIcon = (app) => {
    const isTool = app.app_type === 'productivity';
    const imageUrl = app.images?.medium || app.images?.large || app.images?.small;
    const iconLabel = getTutorialIconLabel(app);
    return (
      <div
        key={app.id}
        className="productivity-app-icon"
        onClick={() => handleAppClick(app.id)}
        title={app.title}
      >
        <div className={`productivity-icon-square ${isTool ? 'has-image' : ''}`}>
          {isTool && imageUrl ? (
            <img src={imageUrl} alt={app.title} />
          ) : (
            <span className="productivity-icon-text">{iconLabel}</span>
          )}
        </div>
        <span className="productivity-icon-name">{app.title}</span>
      </div>
    );
  };

  // Render app icon (for bundles and subject rows). Tool apps use image; productivity use text label.
  const renderAppIcon = (app) => {
    const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo192.png';
    const isProductivity = app.app_type === 'tutorial';
    const isTool = app.app_type === 'productivity';
    const iconLabel = getTutorialIconLabel(app);
    const useTextLabel = isProductivity && !isTool;

    return (
      <div
        key={app.id}
        className="app-icon"
        onClick={() => handleAppClick(app.id)}
        title={app.title}
      >
        <div className={`app-icon-square ${useTextLabel ? 'productivity-icon' : ''}`}>
          {useTextLabel ? (
            <div className="productivity-icon-content">
              <span className="productivity-icon-text">{iconLabel}</span>
            </div>
          ) : (
            <img src={imageUrl} alt={app.title} />
          )}
        </div>
        <span className="app-icon-name">{app.title}</span>
      </div>
    );
  };

  // Render app bundle
  const renderAppBundle = (bundle) => {
    return (
      <div key={bundle.subject} className="app-bundle">
        <h3 className="app-bundle-title">{bundle.subject} Bundle</h3>
        <div className="app-bundle-content">
          <div className="app-bundle-cases">
            {bundle.cases.map(renderAppIcon)}
          </div>
          {bundle.test && (
            <div className="app-bundle-test">
              {renderAppIcon(bundle.test)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render app card (for editing - keeping old function for edit functionality)
  const renderAppCard = (app) => {
    const isEditing = editingApp === app.id;
    const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo192.png';
    const isProductivity = app.app_type === 'tutorial';
    const iconLabel = getTutorialIconLabel(app);
    
    return (
      <div 
        key={app.id} 
        className="app-card"
        onClick={() => !isEditing && handleAppClick(app.id)}
        style={{ cursor: isEditing ? 'default' : 'pointer' }}
      >
        <div className={`app-card-image ${isProductivity ? 'productivity-icon' : ''}`}>
          {isProductivity ? (
            <div className="productivity-icon-content">
              <span className="productivity-icon-text">{iconLabel}</span>
            </div>
          ) : (
            <img src={imageUrl} alt={app.title} />
          )}
        </div>
        <div className="app-card-content">
          <h3 className="app-card-title">{app.title}</h3>
          <p className="app-card-subject">{app.subject} {app.industry ? `• ${app.industry}` : ''}</p>
          
          {isEditing ? (
            <div 
              className="app-edit-form"
              onClick={(e) => e.stopPropagation()}
            >
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="App description"
                rows="3"
                className="app-edit-description"
              />
              <div 
                className="app-edit-ratings"
                onClick={(e) => e.stopPropagation()}
              >
                <label>
                  Rating (0-5):
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={editForm.rating !== undefined ? editForm.rating : 4.5}
                    onChange={(e) => setEditForm({ ...editForm, rating: parseFloat(e.target.value) || 0 })}
                    className="app-edit-rating-input"
                  />
                </label>
                <label>
                  Number of Ratings:
                  <input
                    type="number"
                    min="0"
                    value={editForm.rating_count !== undefined ? editForm.rating_count : 0}
                    onChange={(e) => setEditForm({ ...editForm, rating_count: parseInt(e.target.value) || 0 })}
                    className="app-edit-rating-count-input"
                  />
                </label>
              </div>
              <div 
                className="app-edit-flags"
                onClick={(e) => e.stopPropagation()}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.has_video_interview || false}
                    onChange={(e) => setEditForm({ ...editForm, has_video_interview: e.target.checked })}
                  />
                  Video Interview
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.has_logical_reasoning || false}
                    onChange={(e) => setEditForm({ ...editForm, has_logical_reasoning: e.target.checked })}
                  />
                  Logical Reasoning
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.has_pattern_recognition || false}
                    onChange={(e) => setEditForm({ ...editForm, has_pattern_recognition: e.target.checked })}
                  />
                  Pattern Recognition
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.has_numerical_reasoning || false}
                    onChange={(e) => setEditForm({ ...editForm, has_numerical_reasoning: e.target.checked })}
                  />
                  Numerical Reasoning
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.has_verbal_reasoning || false}
                    onChange={(e) => setEditForm({ ...editForm, has_verbal_reasoning: e.target.checked })}
                  />
                  Verbal Reasoning
                </label>
              </div>
              <div className="app-edit-actions">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSave(app.id);
                  }} 
                  disabled={saving} 
                  className="btn-save"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }} 
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="app-card-description">{app.description || app.case_study_description || 'No description available'}</p>
              {app.assessment_types && app.assessment_types.length > 0 && (
                <div className="app-card-badges">
                  {app.assessment_types.map((type, idx) => (
                    <span key={idx} className="app-badge">{type}</span>
                  ))}
                </div>
              )}
              {app.quiz_title && (
                <p className="app-card-quiz">Quiz: {app.quiz_title}</p>
              )}
              <button onClick={() => handleEdit(app)} className="btn-edit">Edit</button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return null;
  }

  return (
    <div className="apps-store-page netflix-style">
      <header className="apps-store-header">
        <div className="apps-store-header-content">
          <div className="apps-store-left-section">
            <div className="apps-store-logo">
              <Logo_black_iconOnly onClick={() => navigate('/apps-store')} />
            </div>
            <h1 className="apps-store-title">Ivori Apps</h1>
          </div>
          
          <div className="apps-store-header-actions">
            <UserProfileDropdown 
              userName={userName}
              userEmail={userEmail}
            />
          </div>
        </div>
      </header>

      <main className="apps-store-main">
        <div className="apps-store-content">
          {/* Section 1: Tutorial apps grid */}
          {topProductivityApps.length > 0 && (
            <section className="apps-section productivity-grid-section">
              <h2 className="apps-section-title">Tutorial apps</h2>
              <div className="productivity-grid">
                {topProductivityApps.map(renderProductivityIcon)}
              </div>
            </section>
          )}

          {/* Section 2: App Bundles */}
          {appBundles.length > 0 && (
            <section className="apps-section bundles-section">
              <h2 className="apps-section-title">
                App Bundles <span className="apps-count">({appBundles.length})</span>
                <span className="apps-section-arrow">›</span>
              </h2>
              <div className="app-bundles-container">
                {appBundles.map(renderAppBundle)}
              </div>
            </section>
          )}

          {/* Section 3: Apps by Subject */}
          {Object.keys(appsBySubject).length === 0 ? (
            <div className="apps-empty">
              <p>No apps available. Run the populate script to create apps.</p>
            </div>
          ) : (
            Object.entries(appsBySubject).map(([subject, subjectApps]) => (
              <section key={subject} className="apps-section">
                <h2 className="apps-section-title">
                  {subject} <span className="apps-count">({subjectApps.length})</span>
                  <span className="apps-section-arrow">›</span>
                </h2>
                <div className="apps-row-container">
                  <div className="apps-row">
                    {subjectApps.map(renderAppIcon)}
                  </div>
                </div>
              </section>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AppsStorePage;
